import axios from 'axios';

export interface VideoKnowledgePointDto {
  id?: number | string;
  name?: string;
  description?: string;
  [key: string]: any;
}

export interface MindmapResourceResponse {
  /**
   * 总记录数
   */
  count: number;
  /**
   * 下一页URL，如果没有则为null
   */
  next: null;
  /**
   * 上一页URL，如果没有则为null
   */
  previous: null;
  results: MindmapResourceResult[];
  [property: string]: any;
}

export interface MindmapResourceResult {
  /**
   * 如果没有附件
   */
  attachment: null | string;
  created_at: string;
  id: number;
  name: string;
  subject: MindmapSubject;
  chapter?: MindmapChapter | null;
  experiment?: MindmapExperiment | null;
  school?: string;
  updated_at: string;
  [property: string]: any;
}

export interface MindmapSubject {
  description: string;
  id: number;
  name: string;
  [property: string]: any;
}

export interface MindmapChapter {
  description?: string;
  id: number;
  name: string;
  [property: string]: any;
}

export interface MindmapExperiment {
  id: number;
  name: string;
  [property: string]: any;
}

export interface MindmapVideoDto {
  id?: number | string;
  title?: string;
  name?: string;
  knowledgePoint?: string;
  knowledge_point?: string;
  knowledge?: string;
  knowledgeId?: string;
  knowledge_id?: string;
  knowledgePoints?: VideoKnowledgePointDto[];
  knowledge_points?: VideoKnowledgePointDto[];
  attachment?: string | null;
  coverUrl?: string;
  cover_url?: string;
  thumbnail?: string;
  thumbnail_url?: string;
  videoUrl?: string;
  video_url?: string;
  url?: string;
  streamUrl?: string;
  stream_url?: string;
  description?: string;
  summary?: string;
  subject?: MindmapSubject;
  chapter?: MindmapChapter | null;
  experiment?: MindmapExperiment | null;
  school?: string;
  [key: string]: any;
}

export interface MindmapVideo {
  id: string;
  title: string;
  knowledgePoint: string;
  url: string;
  description?: string;
  coverUrl?: string;
  knowledgeId?: string;
}

const defaultBaseUrl = '/backend-api';
const videoApiBaseUrl = (import.meta.env.VITE_VIDEO_API_BASE?.replace(/\/$/, '') ?? defaultBaseUrl);

const videoClient = axios.create({
  baseURL: videoApiBaseUrl,
  timeout: 15000,
});

function extractUrl(dto: MindmapVideoDto): string {
  return dto.url
    ?? dto.videoUrl
    ?? dto.video_url
    ?? dto.attachment
    ?? dto.streamUrl
    ?? dto.stream_url
    ?? '';
}

function extractTitle(dto: MindmapVideoDto, knowledgePoint: string): string {
  return dto.title
    ?? dto.name
    ?? `${knowledgePoint || '未命名知识点'}视频`;
}

function extractCover(dto: MindmapVideoDto): string | undefined {
  return dto.coverUrl
    ?? dto.cover_url
    ?? dto.thumbnail
    ?? dto.thumbnail_url
    ?? undefined;
}

function getKnowledgePointEntries(dto: MindmapVideoDto): Array<{ name: string; id?: string }> {
  const entries = dto.knowledge_points ?? dto.knowledgePoints;
  if (Array.isArray(entries) && entries.length > 0) {
    return entries
      .map((kp): { name: string; id?: string } | null => {
        if (!kp)
          return null;
        const name = typeof kp === 'string' ? kp : kp.name;
        if (!name?.trim())
          return null;
        return {
          name: name.trim(),
          id: typeof kp === 'string' ? undefined : (kp.id ? String(kp.id) : undefined),
        };
      })
      .filter((kp): kp is { name: string; id?: string } => kp !== null);
  }

  if (dto.experiment?.name?.trim())
    return [{ name: dto.experiment.name.trim(), id: String(dto.experiment.id) }];

  if (dto.chapter?.name?.trim())
    return [{ name: dto.chapter.name.trim(), id: String(dto.chapter.id) }];

  if (dto.subject?.name?.trim())
    return [{ name: dto.subject.name.trim(), id: String(dto.subject.id) }];

  return [];
}

function toMindmapVideos(dto: MindmapVideoDto): MindmapVideo[] {
  const url = extractUrl(dto);
  if (!url)
    return [];

  const createVideo = (knowledgePoint: string, kpId?: string): MindmapVideo => {
    const pointLabel = knowledgePoint.trim();
    return {
      id: String(dto.id ?? `${pointLabel}-${kpId ?? url}`),
      title: extractTitle(dto, pointLabel),
      knowledgePoint: pointLabel,
      url,
      description: dto.description ?? dto.summary,
      coverUrl: extractCover(dto),
      knowledgeId: kpId,
    };
  };

  const byKnowledgePoints = getKnowledgePointEntries(dto).map(({ name, id }) => createVideo(name, id));
  if (byKnowledgePoints.length)
    return byKnowledgePoints;

  const fallbackPoint = (dto.knowledgePoint
    ?? dto.knowledge_point
    ?? dto.knowledge
    ?? dto.knowledgeId
    ?? dto.knowledge_id
    ?? dto.experiment?.name
    ?? dto.chapter?.name
    ?? dto.subject?.name
    ?? dto.name
    ?? dto.title)?.trim();

  if (!fallbackPoint)
    return [];

  return [createVideo(fallbackPoint)];
}

function unwrapResponse(data: any): MindmapVideoDto[] {
  if (Array.isArray(data))
    return data as MindmapVideoDto[];

  if (Array.isArray(data?.results))
    return data.results;

  if (Array.isArray(data?.data))
    return data.data;

  return [];
}

export async function fetchMindmapVideos(): Promise<MindmapVideo[]> {
  const response = await videoClient.get<MindmapResourceResponse | MindmapVideoDto[]>('/api/videos/');
  const items = unwrapResponse(response.data)
    .flatMap(toMindmapVideos)
    .filter((item): item is MindmapVideo => Boolean(item?.knowledgePoint));
  return items;
}
